import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Copy, Trash2, PencilRuler, LayoutTemplate } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { StatusBadge } from '../components/common/Badges';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, fmtMoney, fmtDate } from '../lib/format';

const EMPTY = { nombre: '', client_id: '', project_id: '', moneda: 'MXN', exchange_rate: 17.5, iva_percentage: 16 };

export default function CotizacionesPage({ quotationType = 'prefab' }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { canDelete } = useAuth();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ template_id: '', nombre: '', client_id: '', project_id: '' });

  const isPV = quotationType === 'pv';
  const title = isPV ? 'Cotizaciones eHenergia / Prefab PV' : 'Cotizaciones Prefab';

  const { data: cotizaciones, isLoading } = useQuery({
    queryKey: ['quotations', quotationType, statusFilter, search],
    queryFn: async () => {
      const params = { quotation_type: quotationType };
      if (statusFilter !== 'todos') params.status = statusFilter;
      if (search) params.search = search;
      return (await api.get('/quotations', { params })).data;
    },
  });

  const { data: clientes } = useQuery({ queryKey: ['clients', 'todos', ''], queryFn: async () => (await api.get('/clients')).data });
  const { data: proyectos } = useQuery({
    queryKey: ['projects-by-client', form.client_id],
    queryFn: async () => (await api.get('/projects', { params: form.client_id ? { client_id: form.client_id } : {} })).data,
    enabled: dialogOpen,
  });

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/quotations', { ...form, quotation_type: quotationType, exchange_rate: Number(form.exchange_rate), iva_percentage: Number(form.iva_percentage), client_id: form.client_id || null, project_id: form.project_id || null })).data,
    onSuccess: (q) => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      toast.success(`Cotización ${q.quotation_number} creada`);
      setDialogOpen(false);
      navigate(`/cotizaciones/${q.id}/editor`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const cloneMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/quotations/${id}/clone`)).data,
    onSuccess: (q) => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      toast.success(`Clonada como ${q.quotation_number}`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/quotations/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      toast.success('Cotización eliminada');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const { data: templates } = useQuery({
    queryKey: ['templates', quotationType],
    queryFn: async () => (await api.get('/templates', { params: { quotation_type: quotationType } })).data,
    enabled: templateDialogOpen,
  });

  const { data: proyectosTpl } = useQuery({
    queryKey: ['projects-by-client-tpl', templateForm.client_id],
    queryFn: async () => (await api.get('/projects', { params: templateForm.client_id ? { client_id: templateForm.client_id } : {} })).data,
    enabled: templateDialogOpen,
  });

  const createFromTemplateMutation = useMutation({
    mutationFn: async () => (await api.post(`/templates/${templateForm.template_id}/create-quotation`, {
      nombre: templateForm.nombre,
      client_id: templateForm.client_id || null,
      project_id: templateForm.project_id || null,
    })).data,
    onSuccess: (q) => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      toast.success(`Cotización ${q.quotation_number} creada desde plantilla`);
      setTemplateDialogOpen(false);
      navigate(`/cotizaciones/${q.id}/editor`);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (tid) => (await api.delete(`/templates/${tid}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setTemplateForm((f) => ({ ...f, template_id: '' }));
      toast.success('Plantilla eliminada');
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const { data: appSettings } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => (await api.get('/settings')).data,
  });

  const emptyForm = () => ({
    ...EMPTY,
    exchange_rate: appSettings?.exchange_rate_default ?? EMPTY.exchange_rate,
    iva_percentage: appSettings?.iva_default ?? EMPTY.iva_percentage,
  });

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground">Cotizaciones con Análisis de Precio Unitario</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setTemplateForm({ template_id: '', nombre: '', client_id: '', project_id: '' }); setTemplateDialogOpen(true); }} data-testid="from-template-button">
            <LayoutTemplate size={16} className="mr-1.5" /> Desde plantilla
          </Button>
          <Button onClick={() => { setForm(emptyForm()); setDialogOpen(true); }} data-testid="new-quotation-button">
            <Plus size={16} className="mr-1.5" /> Nueva cotización
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o número..." className="pl-9" data-testid="quotations-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="quotations-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estados</SelectItem>
            <SelectItem value="draft">Borrador</SelectItem>
            <SelectItem value="review">En revisión</SelectItem>
            <SelectItem value="finalized">Finalizada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="quotations-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Número</TableHead>
                  <TableHead className="th-label">Nombre</TableHead>
                  <TableHead className="th-label">Cliente</TableHead>
                  <TableHead className="th-label">Estado</TableHead>
                  <TableHead className="th-label">Moneda</TableHead>
                  <TableHead className="th-label text-right">Total</TableHead>
                  <TableHead className="th-label">Actualizada</TableHead>
                  <TableHead className="th-label w-14"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cotizaciones || []).length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Sin cotizaciones. Crea la primera con “Nueva cotización”.</TableCell></TableRow>
                )}
                {(cotizaciones || []).map((q) => (
                  <TableRow key={q.id} className="hover:bg-background/40 cursor-pointer" data-testid={`quotation-row-${q.quotation_number}`} onClick={() => navigate(`/cotizaciones/${q.id}/editor`)}>
                    <TableCell className="font-mono text-[13px]">{q.quotation_number}</TableCell>
                    <TableCell className="max-w-[260px] truncate font-medium">{q.nombre}</TableCell>
                    <TableCell className="text-muted-foreground">{q.cliente_nombre}</TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell className="font-mono text-[13px]">{q.moneda}</TableCell>
                    <TableCell className="money">{fmtMoney(q.totals?.total, q.moneda)}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{fmtDate(q.updated_at)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`quotation-actions-${q.quotation_number}`}>
                            <MoreHorizontal size={15} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/cotizaciones/${q.id}/editor`)} data-testid="action-open-editor">
                            <PencilRuler size={14} className="mr-2" /> Abrir editor
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => cloneMutation.mutate(q.id)} data-testid="action-clone">
                            <Copy size={14} className="mr-2" /> Clonar
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => deleteMutation.mutate(q.id)} data-testid="action-delete">
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="new-quotation-dialog">
          <DialogHeader><DialogTitle>Nueva cotización {isPV ? 'PV' : 'Prefab'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nombre de la cotización *</Label>
              <Input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Ej. Nave industrial fase 2" data-testid="quotation-nombre-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Cliente</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v, project_id: '' }))}>
                  <SelectTrigger data-testid="quotation-client-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {(clientes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Proyecto</Label>
                <Select value={form.project_id} onValueChange={(v) => setForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="quotation-project-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {(proyectos || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre_proyecto}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Moneda de presentación</Label>
                <Select value={form.moneda} onValueChange={(v) => setForm((f) => ({ ...f, moneda: v }))}>
                  <SelectTrigger data-testid="quotation-moneda-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MXN">MXN · Peso mexicano</SelectItem>
                    <SelectItem value="USD">USD · Dólar americano</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de cambio (MXN/USD)</Label>
                <Input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm((f) => ({ ...f, exchange_rate: e.target.value }))} data-testid="quotation-tc-input" />
              </div>
              <div className="space-y-1.5">
                <Label>IVA %</Label>
                <Input type="number" step="0.01" value={form.iva_percentage} onChange={(e) => setForm((f) => ({ ...f, iva_percentage: e.target.value }))} data-testid="quotation-iva-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!form.nombre || createMutation.isPending} data-testid="create-quotation-button">
              Crear y abrir editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: crear desde plantilla */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="from-template-dialog">
          <DialogHeader><DialogTitle>Crear cotización desde plantilla</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Plantilla *</Label>
              <div className="flex gap-2">
                <Select value={templateForm.template_id} onValueChange={(v) => setTemplateForm((f) => ({ ...f, template_id: v }))}>
                  <SelectTrigger data-testid="template-select"><SelectValue placeholder="Selecciona una plantilla" /></SelectTrigger>
                  <SelectContent>
                    {(templates || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                {canDelete && templateForm.template_id && (
                  <Button variant="outline" size="icon" className="shrink-0 text-destructive hover:text-destructive" onClick={() => deleteTemplateMutation.mutate(templateForm.template_id)} data-testid="delete-template-button" title="Eliminar plantilla">
                    <Trash2 size={15} />
                  </Button>
                )}
              </div>
              {(templates || []).length === 0 && (
                <p className="text-[12px] text-muted-foreground">No hay plantillas guardadas. Desde el editor usa “Acciones → Guardar como plantilla”.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Nombre de la nueva cotización *</Label>
              <Input value={templateForm.nombre} onChange={(e) => setTemplateForm((f) => ({ ...f, nombre: e.target.value }))} placeholder="Ej. Nave industrial fase 3" data-testid="template-quotation-nombre-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Cliente</Label>
                <Select value={templateForm.client_id} onValueChange={(v) => setTemplateForm((f) => ({ ...f, client_id: v, project_id: '' }))}>
                  <SelectTrigger data-testid="template-client-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {(clientes || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Proyecto</Label>
                <Select value={templateForm.project_id} onValueChange={(v) => setTemplateForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="template-project-select"><SelectValue placeholder="Opcional" /></SelectTrigger>
                  <SelectContent>
                    {(proyectosTpl || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre_proyecto}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => createFromTemplateMutation.mutate()} disabled={!templateForm.template_id || !templateForm.nombre || createFromTemplateMutation.isPending} data-testid="create-from-template-button">
              Crear y abrir editor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
